/**
 * Klaviyo integration module
 * Handles user identification and event tracking
 */

// Extend the Window interface to include klaviyo
declare global {
  interface Window {
    klaviyo?: {
      identify: (properties: Record<string, any>) => void
      track: (eventName: string, properties?: Record<string, any>) => void
      push: (args: any[]) => void
      isIdentified: () => boolean
    }
    _learnq?: any[]
  }
}

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  const ISPROD = import.meta.env.PROD
  console.log(
    ISPROD ? 'Running in production mode' : 'Running in development mode',
  )
  return ISPROD
}

/**
 * Check if Klaviyo is loaded and available
 */
export function isKlaviyoLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.klaviyo !== 'undefined'
}

/**
 * Wait for Klaviyo to load (with timeout)
 */
export function waitForKlaviyo(timeout = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    if (isKlaviyoLoaded()) {
      resolve(true)
      return
    }

    const startTime = Date.now()
    const interval = setInterval(() => {
      if (isKlaviyoLoaded()) {
        clearInterval(interval)
        resolve(true)
      } else if (Date.now() - startTime > timeout) {
        clearInterval(interval)
        console.warn('Klaviyo failed to load within timeout period')
        resolve(false)
      }
    }, 100)
  })
}

/**
 * Identify a user in Klaviyo
 * @param email - User's email address (required)
 * @param properties - Additional user properties for segmentation
 */
export async function identifyUser(
  email: string,
  properties?: {
    first_name?: string
    last_name?: string
    phone_number?: string
    [key: string]: any
  },
) {
  // Skip in development mode
  if (!isProduction()) {
    console.log('[Klaviyo Dev] Would identify user:', email, properties)
    return
  }

  const loaded = await waitForKlaviyo()
  if (!loaded || !window.klaviyo) {
    console.warn('Klaviyo not available for user identification')
    return
  }

  try {
    window.klaviyo.identify({
      email,
      ...properties,
    })
  } catch (error) {
    console.error('Error identifying user in Klaviyo:', error)
  }
}

/**
 * Track a custom event in Klaviyo
 * @param eventName - Name of the event to track
 * @param properties - Event properties
 */
export async function trackEvent(
  eventName: string,
  properties?: Record<string, any>,
) {
  // Skip in development mode
  if (!isProduction()) {
    console.log('[Klaviyo Dev] Would track event:', eventName, properties)
    return
  }

  const loaded = await waitForKlaviyo()
  if (!loaded || !window.klaviyo) {
    console.warn('Klaviyo not available for event tracking')
    return
  }

  try {
    window.klaviyo.track(eventName, properties || {})
  } catch (error) {
    console.error(`Error tracking event "${eventName}" in Klaviyo:`, error)
  }
}

/**
 * Track user signup event
 */
export async function trackSignUp(email: string, method: 'email' | 'google') {
  await trackEvent('Signed Up', {
    email,
    signup_method: method,
    signup_date: new Date().toISOString(),
  })
}

/**
 * Track user login event
 */
export async function trackLogin(email: string, method: 'email' | 'google') {
  await trackEvent('Logged In', {
    email,
    login_method: method,
    login_date: new Date().toISOString(),
  })
}

/**
 * Reset Klaviyo tracking (useful for logout)
 */
export function resetTracking() {
  // Skip in development mode
  if (!isProduction()) {
    console.log('[Klaviyo Dev] Would reset tracking')
    return
  }

  if (typeof window !== 'undefined' && window._learnq) {
    window._learnq = []
  }
}
